/**
 * web/src/api/gates.ts
 * Typed clients for /api/gates/* (API-02). Server contract: src/pixel/api/gates.py.
 *
 * Server-side role-filters GET /pending — admin sees all, others see only
 * gates whose gateRole === their role. The client NEVER trusts its own
 * role filter (T-05-02-04); the server is authoritative.
 *
 * `edits` is opaque (Record<string, unknown>) per ApproveBody.default({}).
 * The shape varies by step (e.g., welcome_email gate vs. supplementary_review)
 * and is validated server-side by the workflow definition's onApproval.
 */
import { api } from '../lib/api';
import type { StepExecution, Workflow, EventRef } from './types';

/**
 * GET /api/gates/pending row: a StepExecution joined with a workflow
 * projection (id, displayName, type, eventId, status, event).
 */
export interface PendingGate extends StepExecution {
  workflow: Pick<Workflow, 'id' | 'displayName' | 'type' | 'eventId' | 'status'> & {
    event: EventRef;
  };
}

/**
 * Lists gates currently waiting, scoped to the caller's accessible events
 * (admin sees all). Ordered oldest-first (createdAt asc) so the queue surfaces
 * the most overdue work first.
 *
 * @param eventId optional — narrow to a single event so the Approvals tab
 *        honors the dashboard's active-event selection (mirrors listWorkflows)
 * @returns PendingGate[]
 * @throws ApiClientError on non-2xx
 * @sideEffects HTTP GET
 * @idempotent yes
 */
export async function fetchPendingGates(eventId?: string): Promise<PendingGate[]> {
  const params = new URLSearchParams();
  if (eventId) params.set('eventId', eventId);
  const qs = params.toString();
  return api<PendingGate[]>(`/api/gates/pending${qs ? `?${qs}` : ''}`);
}

/** POST /api/gates/:wf/:idx/approve body. */
export interface ApproveGateBody {
  /** Opaque object — shape depends on the step's gate definition. */
  edits: Record<string, unknown>;
}

/**
 * Approves a waiting gate; the engine advances the workflow on the next
 * tick (setImmediate) so this returns 200 BEFORE the next step runs
 * (Pitfall 3).
 *
 * @param workflowId workflow id
 * @param stepIndex zero-based step index
 * @param body { edits } — passed verbatim to the step's onApproval handler
 * @returns { ok: true } on success
 * @throws ApiClientError(400) VALIDATION_ERROR if edits violate the schema;
 *         (403) if the caller's role doesn't match step.gateRole;
 *         (409) INVALID_STATE_TRANSITION if the step isn't waiting_approval
 * @sideEffects HTTP POST; engine advances the workflow asynchronously
 * @idempotent no — second call against an already-approved gate 409s
 */
export async function approveGate(
  workflowId: string,
  stepIndex: number,
  body: ApproveGateBody,
): Promise<{ ok: true }> {
  return api(`/api/gates/${workflowId}/${stepIndex}/approve`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** POST /api/gates/:wf/:idx/reject body. reason is required. */
export interface RejectGateBody {
  reason: string;
}

/**
 * Rejects a waiting gate. Step status → 'rejected'; workflow stays paused
 * so the reviewer can edit and resubmit.
 *
 * @param workflowId workflow id
 * @param stepIndex zero-based step index
 * @param body { reason } — non-empty rejection reason
 * @returns { ok: true } on success
 * @throws ApiClientError(400) VALIDATION_ERROR if reason missing;
 *         (409) INVALID_STATE_TRANSITION if not currently waiting_approval
 * @sideEffects HTTP POST
 * @idempotent no
 */
export async function rejectGate(
  workflowId: string,
  stepIndex: number,
  body: RejectGateBody,
): Promise<{ ok: true }> {
  return api(`/api/gates/${workflowId}/${stepIndex}/reject`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** POST /api/gates/:wf/:idx/skip body. Same shape as approve. */
export interface SkipGateBody {
  edits: Record<string, unknown>;
}

/**
 * Skips a waiting email gate — the reviewer sent the email outside the
 * system and just wants the workflow to advance. Server invokes the step's
 * onSkip handler (writes Tracker, no Gmail send). Receipt steps following
 * a skipped email auto-complete via cascade.
 *
 * @param workflowId workflow id
 * @param stepIndex zero-based step index
 * @param body { edits } — passed verbatim to the step's onSkip handler
 * @returns { ok: true }
 * @throws ApiClientError(409) INVALID_STATE_TRANSITION if step has no onSkip
 *         or is not in waiting_approval
 * @sideEffects HTTP POST; engine advances the workflow asynchronously
 * @idempotent no
 */
export async function skipGate(
  workflowId: string,
  stepIndex: number,
  body: SkipGateBody,
): Promise<{ ok: true }> {
  return api(`/api/gates/${workflowId}/${stepIndex}/skip`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Returns a rejected gate to 'waiting_approval' so the reviewer can edit
 * and approve again.
 *
 * @param workflowId workflow id
 * @param stepIndex zero-based step index
 * @returns { ok: true } on success
 * @throws ApiClientError(409) INVALID_STATE_TRANSITION if not currently rejected
 * @sideEffects HTTP POST
 * @idempotent yes — resubmitting an already-waiting gate is a no-op (engine returns ok)
 */
export async function resubmitGate(workflowId: string, stepIndex: number): Promise<{ ok: true }> {
  return api(`/api/gates/${workflowId}/${stepIndex}/resubmit`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/** POST /api/gates/:wf/:idx/regenerate body (ops picker + card gate actions). */
export interface RegenerateGateBody {
  /** Chosen template sectionKey (the ops picker is single-select). */
  selected: string[];
  /** Card gate: '' = fresh drafts | 'finalize' | 'build_card' | 'back_to_final' | 'back'. */
  action?: string;
  /** Card gate finalize: the picked draft index. */
  chosenCandidate?: string;
  /** Card gate finalize: palette color name or 'random'. */
  highlightColor?: string;
  /** Card gate back/back_to_final: also retract what the advance published
   *  (the cards board's undo-toast path). */
  unpublish?: boolean;
  /** Speaker email gates: throw the current draft away and recompose it from
   *  the event's CURRENT templates/defaults (Approvals "Regenerate", single or
   *  bulk). Nothing is sent; the gate stays waiting_approval; no notification. */
  redraft?: boolean;
}

/** Fresh gate body + context returned by the regenerate endpoint. */
export interface RegenerateGateResult {
  ok: true;
  /** Absent only for non-email gates (the card gate's data-gate payload). */
  email: { to: string; cc?: string; bcc?: string; subject: string; body: string } | null;
  /** Same shape as email-gate gateData.context (rationale + templatePicker). */
  context: Record<string, unknown>;
}

/**
 * Recomputes an ops gate's email body from the reviewer's section picks
 * WITHOUT advancing the workflow — the gate stays 'waiting_approval'. The
 * server re-persists the gate and returns the fresh { email, context } so the
 * dashboard reseeds the editor in place. A live draft wins over a server
 * refetch (F14), so the caller MUST reseed subject/body from this response
 * rather than relying on a workflow re-fetch.
 *
 * @param workflowId workflow id
 * @param stepIndex zero-based step index (the ops gate)
 * @param body { selected } — forwarded to the step's regenerate hook
 * @returns { ok, email, context } — the recomputed gate payload
 * @throws ApiClientError(403) on gate-role mismatch;
 *         (409) INVALID_STATE_TRANSITION if the step isn't waiting_approval or
 *         isn't regenerable
 * @sideEffects HTTP POST; server re-persists gateSchema/gateData (no advance)
 * @idempotent yes — same picks yield the same composed body
 */
export async function regenerateGate(
  workflowId: string,
  stepIndex: number,
  body: RegenerateGateBody,
): Promise<RegenerateGateResult> {
  return api<RegenerateGateResult>(`/api/gates/${workflowId}/${stepIndex}/regenerate`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}
